import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import {
  listEventLogs,
  type AdminEventLogItem,
} from 'api/admin/eventLogs/listEventLogs'
import { userEventLogsKey } from './keys'

type UseUserEventLogsQueryArgs = {
  userId: number
  startsAt: string
  endsAt: string
}

type UseUserEventLogsQueryData = AdminEventLogItem[]
type UseUserEventLogsQueryKey = ReturnType<typeof userEventLogsKey>

type UseUserEventLogsQueryOptions = Partial<
  UseQueryOptions<
    UseUserEventLogsQueryData,
    Error,
    UseUserEventLogsQueryData,
    UseUserEventLogsQueryKey
  >
>

// Cap pages walked for a single user's date range; unbounded browsing should use the dedicated /review/event-logs page.
const MAX_PAGES = 10
const PAGE_LIMIT = 100

async function fetchAllUserEventLogs(
  args: UseUserEventLogsQueryArgs
): Promise<UseUserEventLogsQueryData> {
  const items: AdminEventLogItem[] = []
  let cursor: string | undefined
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = await listEventLogs({ ...args, limit: PAGE_LIMIT, cursor })
    items.push(...page.items)
    if (!page.nextCursor) return items
    cursor = page.nextCursor
  }
  return items
}

// Fetches every event log for a user in a date range, walking `/admin/event_logs` cursor pages internally and capped at MAX_PAGES * PAGE_LIMIT rows to preserve the legacy graphql endpoint's "everything in range" behavior.
export function useUserEventLogsQuery(
  args: UseUserEventLogsQueryArgs,
  options: UseUserEventLogsQueryOptions = {}
) {
  return useQuery<
    UseUserEventLogsQueryData,
    Error,
    UseUserEventLogsQueryData,
    UseUserEventLogsQueryKey
  >({
    ...options,
    queryKey: userEventLogsKey(args),
    queryFn: () => fetchAllUserEventLogs(args),
  })
}
